import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  RefreshCcw,
  Trash2,
  AlertTriangle,
  Search,
  Layers,
  XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCurrency(v) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function ageHuman(iso) {
  if (!iso) return "—";
  const sec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (sec < 60) return `${Math.floor(sec)}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function AdminPendingCleanup() {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(null); // single txn to cancel
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [reasonInput, setReasonInput] = useState("Not a real payment — duplicate / abandoned");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payments/admin/diagnostic/all-pending-txns`, {
        withCredentials: true,
      });
      setAllRows(res.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pending payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = allRows.filter((r) => {
    if (!search) return true;
    const needle = search.toLowerCase();
    return [r.parent_name, r.parent_email, r.payment_reference, r.id]
      .filter(Boolean)
      .some((s) => String(s).toLowerCase().includes(needle));
  });

  const duplicateRows = filteredRows.filter((r) => r.is_duplicate);
  const uniqueRows = filteredRows.filter((r) => !r.is_duplicate);

  const previewBulk = async () => {
    try {
      const res = await axios.post(
        `${API}/payments/admin/diagnostic/cancel-duplicate-pending-txns`,
        { dry_run: true },
        { withCredentials: true }
      );
      setBulkPreview(res.data);
      setBulkConfirmOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to preview bulk cancel");
    }
  };

  const executeBulk = async () => {
    try {
      const res = await axios.post(
        `${API}/payments/admin/diagnostic/cancel-duplicate-pending-txns`,
        { dry_run: false },
        { withCredentials: true }
      );
      toast.success(
        `Cancelled ${res.data.cancelled_count} duplicate pending transaction${
          res.data.cancelled_count === 1 ? "" : "s"
        } across ${res.data.groups_affected} group${res.data.groups_affected === 1 ? "" : "s"}.`
      );
      setBulkConfirmOpen(false);
      setBulkPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel duplicates");
    }
  };

  const cancelSingle = async (txn, reason) => {
    setBusyIds((prev) => new Set(prev).add(txn.id));
    try {
      await axios.post(
        `${API}/payments/admin/diagnostic/cancel-pending-txn`,
        { transaction_id: txn.id, reason },
        { withCredentials: true }
      );
      toast.success(`Cancelled txn ${txn.id.slice(0, 8)}…`);
      setConfirmCancel(null);
      // Optimistically remove
      setAllRows((rows) => rows.filter((r) => r.id !== txn.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(txn.id);
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" className="text-white" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-rose-400" />
              Pending Payment Cleanup
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn">
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCcw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <Card className="p-4 mb-4 bg-amber-900/20 border-amber-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-slate-200">
              <p className="font-semibold mb-1">Read this before you start</p>
              <ul className="list-disc list-inside space-y-1 text-slate-300 text-xs">
                <li>Cancelling a pending row is a <span className="text-white font-medium">soft cancel</span> — the record stays in the database with status=cancelled for audit, the parent just no longer sees it as owed.</li>
                <li>Real successful payments are protected — the API rejects any cancel attempt against a paid/verified/approved txn.</li>
                <li>Use <span className="text-white font-medium">Bulk-cancel duplicates</span> to clean up the "5 visits → 5 rows" pattern instantly. Singletons stay untouched.</li>
                <li>Manual cancel is for true one-offs (test rows, abandoned attempts, parents who'll never pay).</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Summary + bulk action */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Card className="p-4 bg-slate-900/50">
            <div className="text-xs text-slate-400 uppercase">Total open pending</div>
            <div className="text-3xl font-bold text-white mt-1" data-testid="sum-total">
              {filteredRows.length}
            </div>
          </Card>
          <Card className="p-4 bg-rose-900/20 border-rose-500/30">
            <div className="text-xs text-rose-300 uppercase">Duplicates</div>
            <div className="text-3xl font-bold text-rose-200 mt-1" data-testid="sum-duplicates">
              {duplicateRows.length}
            </div>
          </Card>
          <Card className="p-4 bg-slate-900/50 flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-400 uppercase">Bulk cleanup</div>
              <div className="text-xs text-slate-500 mt-1">
                Cancels all duplicate pending rows, keeps newest per group.
              </div>
            </div>
            <Button
              onClick={previewBulk}
              disabled={duplicateRows.length === 0}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bulk-cancel-btn"
            >
              <Layers className="w-4 h-4 mr-2" />
              Preview
            </Button>
          </Card>
        </div>

        {/* Search */}
        <Card className="p-3 mb-4 bg-slate-900/50">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder="Search by parent name, email, reference, or txn id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-800 border-slate-700 text-white"
              data-testid="search-input"
            />
          </div>
        </Card>

        {/* Table */}
        <Card className="bg-slate-900/50 overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              No pending payments to clean up. 🎉
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pending-txns-table">
                <thead className="bg-slate-800/60 text-slate-300">
                  <tr>
                    <th className="text-left p-3">Parent / Reference</th>
                    <th className="text-left p-3">Type / Method</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Created</th>
                    <th className="text-left p-3">Flag</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-t border-slate-800 ${
                        r.is_duplicate ? "bg-rose-900/10" : ""
                      } hover:bg-slate-800/40`}
                      data-testid={`pending-row-${r.id}`}
                    >
                      <td className="p-3">
                        <div className="font-medium text-white">{r.parent_name || "—"}</div>
                        <div className="text-xs text-slate-400">{r.parent_email}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                          {r.payment_reference}
                        </div>
                      </td>
                      <td className="p-3 text-xs text-slate-300">
                        <div className="capitalize">
                          {(r.payment_type || "—").replace(/_/g, " ")}
                        </div>
                        <div className="text-slate-500 capitalize">{r.payment_method || "—"}</div>
                      </td>
                      <td className="p-3 text-right text-white font-medium">
                        {formatCurrency(r.amount)}
                      </td>
                      <td className="p-3 text-xs">
                        <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                          {r.payment_status || r.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-slate-400">{ageHuman(r.created_at)}</td>
                      <td className="p-3">
                        {r.is_duplicate ? (
                          <Badge
                            variant="outline"
                            className="border-rose-500/40 text-rose-300"
                            title={`This is part of a group of ${r.duplicate_group_size} pending rows for the same parent/reference.`}
                          >
                            duplicate ({r.duplicate_group_size})
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-500">unique</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                          disabled={busyIds.has(r.id)}
                          onClick={() => setConfirmCancel(r)}
                          data-testid={`cancel-btn-${r.id}`}
                        >
                          {busyIds.has(r.id) ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="w-3 h-3 mr-1" />
                              Cancel
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Single-cancel confirmation */}
      <Dialog open={!!confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Cancel this pending payment?</DialogTitle>
            <DialogDescription className="text-slate-400">
              This is a soft cancel — the row stays for audit but the parent no longer sees it as
              owed. Real paid transactions cannot be cancelled this way.
            </DialogDescription>
          </DialogHeader>
          {confirmCancel && (
            <div className="bg-slate-800/50 rounded p-3 text-sm space-y-1">
              <div>
                <span className="text-slate-400">Parent:</span>{" "}
                <span className="text-white">{confirmCancel.parent_name}</span>
              </div>
              <div>
                <span className="text-slate-400">Reference:</span>{" "}
                <span className="text-white font-mono">{confirmCancel.payment_reference}</span>
              </div>
              <div>
                <span className="text-slate-400">Amount:</span>{" "}
                <span className="text-white">{formatCurrency(confirmCancel.amount)}</span>
              </div>
              <div>
                <span className="text-slate-400">Created:</span>{" "}
                <span className="text-white">{ageHuman(confirmCancel.created_at)}</span>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs text-slate-400 mb-1">Reason (audit log)</label>
            <Input
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white text-sm"
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmCancel(null)}
              className="border-slate-600"
              data-testid="cancel-dialog-cancel"
            >
              Keep it
            </Button>
            <Button
              onClick={() => cancelSingle(confirmCancel, reasonInput)}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="cancel-dialog-confirm"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel pending
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-cancel confirmation */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cancel duplicate pending payments?</DialogTitle>
            <DialogDescription className="text-slate-400">
              For each group of duplicate pending rows, the newest row is kept active (so the parent
              still has a path to pay). Older duplicates are soft-cancelled with reason{" "}
              <span className="font-mono text-slate-300">duplicate_qr_visit</span>.
            </DialogDescription>
          </DialogHeader>
          {bulkPreview && (
            <div className="bg-slate-800/50 rounded p-3 text-sm space-y-2">
              <div>
                Groups affected:{" "}
                <span className="font-bold text-white">{bulkPreview.groups_affected}</span>
              </div>
              <div>
                Will cancel:{" "}
                <span className="font-bold text-rose-300">{bulkPreview.would_cancel_count}</span>{" "}
                row{bulkPreview.would_cancel_count === 1 ? "" : "s"}
              </div>
              <div>
                Will keep:{" "}
                <span className="font-bold text-emerald-300">{bulkPreview.would_keep_count}</span>{" "}
                row{bulkPreview.would_keep_count === 1 ? "" : "s"} (newest per group)
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkConfirmOpen(false)}
              className="border-slate-600"
              data-testid="bulk-dialog-cancel"
            >
              Back out
            </Button>
            <Button
              onClick={executeBulk}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={!bulkPreview || bulkPreview.would_cancel_count === 0}
              data-testid="bulk-dialog-confirm"
            >
              <Layers className="w-4 h-4 mr-2" />
              Cancel {bulkPreview?.would_cancel_count || 0} duplicate{bulkPreview?.would_cancel_count === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
